import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import FormModal from '../../components/ui/FormModal';
import {
    deleteGroup, emptyGroup, loadGroups, loadProducts, ModifierGroup, ProductOption, saveGroup, selectionRule, validateGroup,
} from './modifiers';

const money = (value: number) => Number(value ?? 0).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Grupos de modificadores ("Término de la carne", "Extras") y los productos que los preguntan al vender
export const ModifiersPage = () => {

    const navigate = useNavigate();

    const [groups, setGroups] = useState<ModifierGroup[]>([]);
    const [products, setProducts] = useState<ProductOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    const [editing, setEditing] = useState<ModifierGroup | null>(null);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [productSearch, setProductSearch] = useState('');
    const [toDelete, setToDelete] = useState<ModifierGroup | null>(null);

    const productById = useMemo(() => new Map(products.map(p => [p.id, p])), [products]);

    const refresh = async () => {
        setLoading(true);
        setError('');
        try {
            const [g, p] = await Promise.all([loadGroups(), loadProducts()]);
            setGroups(g);
            setProducts(p);
        } catch (e: any) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, []);

    const openEditor = (group?: ModifierGroup) => {
        setEditing(group ? { ...group, options: group.options.map(o => ({ ...o })), productIds: [...group.productIds] } : emptyGroup());
        setFormError('');
        setProductSearch('');
    };

    const update = (changes: Partial<ModifierGroup>) => setEditing(g => g ? { ...g, ...changes } : g);

    const updateOption = (index: number, changes: Partial<ModifierGroup['options'][number]>) =>
        setEditing(g => g ? { ...g, options: g.options.map((o, i) => i === index ? { ...o, ...changes } : o) } : g);

    const moveOption = (index: number, delta: number) => setEditing(g => {
        if (!g) return g;
        const options = [...g.options];
        const target = index + delta;
        if (target < 0 || target >= options.length) return g;
        [options[index], options[target]] = [options[target], options[index]];
        return { ...g, options };
    });

    const toggleProduct = (id: number) => setEditing(g => g ? {
        ...g,
        productIds: g.productIds.includes(id) ? g.productIds.filter(p => p !== id) : [...g.productIds, id],
    } : g);

    const handleSave = async () => {
        if (!editing) return;
        const invalid = validateGroup(editing);
        if (invalid) {
            setFormError(invalid);
            return;
        }
        setSaving(true);
        setFormError('');
        try {
            await saveGroup({ ...editing, options: editing.options.map(o => ({ ...o, name: o.name.trim() })) });
            setNotice(editing.id ? 'Grupo actualizado.' : 'Grupo creado.');
            setEditing(null);
            await refresh();
        } catch (e: any) {
            setFormError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!toDelete?.id) return;
        setSaving(true);
        try {
            await deleteGroup(toDelete.id);
            setNotice('Grupo eliminado.');
            setToDelete(null);
            await refresh();
        } catch (e: any) {
            setError(e.message);
            setToDelete(null);
        } finally {
            setSaving(false);
        }
    };

    const filteredProducts = useMemo(() => {
        const term = productSearch.trim().toLowerCase();
        return products.filter(p => !term || p.name.toLowerCase().includes(term) || p.menu.toLowerCase().includes(term));
    }, [products, productSearch]);

    // =============================
    // Editor
    // =============================
    const editor = editing && (
        <FormModal
            title={editing.id ? 'Editar grupo de modificadores' : 'Nuevo grupo de modificadores'}
            subtitle="Las opciones se preguntan al agregar el producto a una orden."
            onClose={() => setEditing(null)}
            onSave={handleSave}
            saving={saving}
            error={formError}
            size="lg"
        >
            <div className="row g-3 mb-3">
                <div className="col-12 col-md-6">
                    <label className="form-label small fw-semibold">Nombre del grupo</label>
                    <input className="form-control" maxLength={80} placeholder="Ej.: Término de la carne" autoFocus
                        value={editing.name} onChange={e => update({ name: e.target.value })} />
                </div>
                <div className="col-6 col-md-3">
                    <label className="form-label small fw-semibold">Mínimo a elegir</label>
                    <input type="number" min={0} className="form-control" value={editing.minSelect}
                        onChange={e => update({ minSelect: Math.max(0, parseInt(e.target.value || '0')) })} />
                </div>
                <div className="col-6 col-md-3">
                    <label className="form-label small fw-semibold">Máximo a elegir</label>
                    <input type="number" min={0} className="form-control" value={editing.maxSelect}
                        onChange={e => update({ maxSelect: Math.max(0, parseInt(e.target.value || '0')) })} />
                </div>
                <div className="col-12">
                    <div className="small text-muted">
                        <i className="bi bi-info-circle me-1" />
                        {selectionRule(editing)}. Mínimo 1 o más lo hace obligatorio; máximo 0 es sin límite.
                    </div>
                </div>
            </div>

            <h6 className="mb-1">Opciones</h6>
            <p className="small text-muted mb-2">
                Sin producto, la opción es gratis y se anota en la comanda. Ligada a un producto, es un extra que se cobra con el precio,
                impuesto e inventario de ese producto.
            </p>
            {editing.options.map((option, index) => {
                const linked = option.productId ? productById.get(option.productId) : undefined;
                return (
                    <div key={index} className="d-flex flex-wrap gap-2 align-items-center mb-2">
                        <input className="form-control" style={{ flex: '2 1 180px' }} maxLength={80} placeholder="Ej.: Término medio"
                            value={option.name} onChange={e => updateOption(index, { name: e.target.value })} />
                        <select className="form-select" style={{ flex: '2 1 200px' }} value={option.productId ?? ''}
                            onChange={e => updateOption(index, { productId: e.target.value ? Number(e.target.value) : null })}>
                            <option value="">Gratis (sin producto)</option>
                            {option.productId && !linked && <option value={option.productId}>{option.productName ?? 'Producto no disponible'}</option>}
                            {products.map(p => <option key={p.id} value={p.id}>{p.name} · {money(p.price)}</option>)}
                        </select>
                        <div className="btn-group">
                            <button type="button" className="btn btn-outline-secondary" title="Subir" disabled={index === 0} onClick={() => moveOption(index, -1)}>
                                <i className="bi bi-arrow-up" />
                            </button>
                            <button type="button" className="btn btn-outline-secondary" title="Bajar" disabled={index === editing.options.length - 1} onClick={() => moveOption(index, 1)}>
                                <i className="bi bi-arrow-down" />
                            </button>
                            <button type="button" className="btn btn-outline-danger" title="Quitar opción" disabled={editing.options.length === 1}
                                onClick={() => update({ options: editing.options.filter((_, i) => i !== index) })}>
                                <i className="bi bi-trash" />
                            </button>
                        </div>
                    </div>
                );
            })}
            <button type="button" className="btn btn-link px-0 mb-3" onClick={() => update({ options: [...editing.options, { name: '', productId: null }] })}>
                <i className="bi bi-plus-lg me-1" /> Agregar opción
            </button>

            <h6 className="mb-1">Productos que lo preguntan <span className="badge bg-light text-dark border ms-1">{editing.productIds.length}</span></h6>
            <input className="form-control form-control-sm mb-2" placeholder="Buscar producto o menú"
                value={productSearch} onChange={e => setProductSearch(e.target.value)} />
            <div className="border rounded" style={{ maxHeight: 240, overflowY: 'auto' }}>
                {filteredProducts.length === 0 && <div className="small text-muted p-2">No hay productos que coincidan.</div>}
                {filteredProducts.map(p => (
                    <label key={p.id} className="d-flex align-items-center gap-2 px-2 py-1 border-bottom small" style={{ cursor: 'pointer' }}>
                        <input type="checkbox" className="form-check-input mt-0" checked={editing.productIds.includes(p.id)} onChange={() => toggleProduct(p.id)} />
                        <span className="flex-grow-1">{p.name}</span>
                        <span className="text-muted">{p.menu}</span>
                    </label>
                ))}
            </div>
        </FormModal>
    );

    // =============================
    // Render
    // =============================
    return (
        <div className="mt-5 container-fluid px-4" style={{ flex: 1, marginTop: 10 }}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-3 gap-2">
                <div>
                    <button type="button" className="btn btn-link text-decoration-none px-0 mb-1" onClick={() => navigate('/products')}>
                        <i className="bi bi-arrow-left me-1" /> Productos y menús
                    </button>
                    <h3 className="mb-1">Modificadores y extras</h3>
                    <p className="text-muted mb-0">Opciones que se preguntan al vender un producto: término de la carne, ingredientes, extras con precio.</p>
                </div>
                <button type="button" className="btn btn-primary" onClick={() => openEditor()} disabled={loading}>
                    <i className="bi bi-plus-lg me-1" /> Nuevo grupo
                </button>
            </div>

            {error && (
                <div className="alert alert-danger d-flex align-items-start gap-2 py-2">
                    <i className="bi bi-exclamation-circle mt-1" />
                    <div className="small">{error}</div>
                </div>
            )}
            {notice && (
                <div className="alert alert-success d-flex align-items-center gap-2 py-2">
                    <i className="bi bi-check-circle" />
                    <div className="small flex-grow-1">{notice}</div>
                    <button type="button" className="btn-close btn-sm" onClick={() => setNotice('')} />
                </div>
            )}

            {loading ? (
                <div className="text-center text-muted py-5"><span className="spinner-border spinner-border-sm me-2" />Cargando...</div>
            ) : groups.length === 0 ? (
                <div className="card border-0 shadow-sm">
                    <div className="card-body text-center py-5">
                        <i className="bi bi-ui-checks-grid fs-1 text-muted" />
                        <h5 className="mt-2">Todavía no hay modificadores</h5>
                        <p className="text-muted">Cree un grupo, por ejemplo "Término de la carne" con las opciones Medio y Bien cocido, y asígnelo a sus productos.</p>
                        <button type="button" className="btn btn-primary" onClick={() => openEditor()}>
                            <i className="bi bi-plus-lg me-1" /> Nuevo grupo
                        </button>
                    </div>
                </div>
            ) : (
                <div className="row g-3">
                    {groups.map(group => (
                        <div key={group.id} className="col-12 col-md-6 col-xl-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body d-flex flex-column">
                                    <div className="d-flex justify-content-between align-items-start gap-2">
                                        <div>
                                            <h6 className="mb-0">{group.name}</h6>
                                            <span className={`badge ${group.minSelect > 0 ? 'bg-warning-subtle text-warning-emphasis' : 'bg-light text-muted border'}`}>
                                                {selectionRule(group)}
                                            </span>
                                        </div>
                                        <div className="btn-group btn-group-sm">
                                            <button type="button" className="btn btn-outline-secondary" title="Editar" onClick={() => openEditor(group)}>
                                                <i className="bi bi-pencil" />
                                            </button>
                                            <button type="button" className="btn btn-outline-danger" title="Eliminar" onClick={() => setToDelete(group)}>
                                                <i className="bi bi-trash" />
                                            </button>
                                        </div>
                                    </div>
                                    <ul className="list-unstyled small my-3 flex-grow-1">
                                        {group.options.map(option => (
                                            <li key={option.id} className="d-flex justify-content-between border-bottom py-1">
                                                <span>
                                                    {option.name}
                                                    {option.productId && option.productActive === false &&
                                                        <span className="text-danger ms-1" title="El producto ligado no está activo: la opción no se ofrece">
                                                            <i className="bi bi-exclamation-triangle" />
                                                        </span>}
                                                </span>
                                                <span className="text-muted">{option.productId ? `+${money(option.price ?? 0)}` : 'Gratis'}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="small text-muted">
                                        <i className="bi bi-bag me-1" />
                                        {group.productIds.length === 0
                                            ? 'No está asignado a ningún producto'
                                            : group.productIds.map(id => productById.get(id)?.name).filter(Boolean).slice(0, 4).join(', ')
                                            + (group.productIds.length > 4 ? ` y ${group.productIds.length - 4} más` : '')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editor}

            {toDelete && (
                <FormModal
                    title="Eliminar grupo"
                    onClose={() => setToDelete(null)}
                    onSave={handleDelete}
                    saving={saving}
                    saveLabel="Eliminar"
                    size="sm"
                >
                    <p className="mb-0">
                        ¿Eliminar <strong>{toDelete.name}</strong>? Deja de preguntarse en {toDelete.productIds.length} producto(s).
                        Las órdenes ya hechas no cambian.
                    </p>
                </FormModal>
            )}
        </div>
    );
};
